'use client';

import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { formatFormikError } from 'fetcher-session';
import { Form, Formik } from 'formik';
import {
  FormikErrorMessage,
  FormikField,
  FormikFieldError,
  FormikNumberInput,
  FormikSelectField,
  FormikTextArea,
} from 'formik-ui';
import React, { useMemo, useState } from 'react';
import { Button, Label, Loader } from 'ui';
import { displayPrice } from 'utils';
import * as yup from 'yup';
import {
  postMakeCounterOffer,
  postMakeSellerOffer,
} from '~/api/swapDetails/api';
import { swapByIdQuery } from '~/api/swapDetails/queries';
import AuctionEndText from '~/components/ItemDetails/AuctionEndText';
import CalculateItemShipping from '~/components/ItemDetails/CalculateItemShipping';
import { ModalProps } from '~/components/Modal/ModalsGroup';
import NewModalBody from '~/components/NewModal/NewModalBody';
import SwapActionRejectOffer from '~/components/SwapDetails/Actions/SwapActionRejectOffer';
import { useSession } from '~/contexts/SessionContext';
import useAddToCart from '~/hooks/cart/useAddToCart';
import useBuyerOrSeller from '~/hooks/swapDetails/useBuyerOrSeller';
import useRequestCheckout from '~/hooks/useRequestCheckout';
import { fireSentMessage } from '~/services/analytics/events/swaps';
import getCurrentOffer from '~/utils/getCurrentOffer';

function modalTitle(action: ModalProps<'makeOffer'>['action']) {
  switch (action) {
    case 'addToCart':
      return 'Add to Cart';

    case 'sellerOffer':
    case 'makeOffer':
      return 'Make Offer';

    case 'counterOffer':
      return 'Counter Offer';

    case 'buyNow':
      return 'Buy Now';
  }
}

function modalCTA(action: ModalProps<'makeOffer'>['action']) {
  switch (action) {
    case 'addToCart':
      return 'Add to Cart';

    case 'sellerOffer':
      return 'Send Private Offer';

    case 'makeOffer':
      return 'Make Offer';

    case 'counterOffer':
      return 'Counter Offer';

    case 'buyNow':
      return 'Buy Now';
  }
}

interface MakeOfferModalFormValues {
  selectedVariationId?: string | number;
  offerAmount: null | number;
  offerMessage?: string;
}

const MakeOfferModal: React.FC<ModalProps<'makeOffer'>> = ({
  action,
  item,
  onRequestClose,
  onSuccessfullyMadeOffer,
  selectedVariationId: initialSelectedVariationId,
  swapId,
  betaCheckout,
}) => {
  const session = useSession();
  const { buyNow, makeOffer } = useRequestCheckout(item);
  const { onAddToCartAsync } = useAddToCart(item);
  const { data: swapDetails } = useQuery({
    ...swapByIdQuery(undefined, swapId!),
    enabled: !!swapId,
  });
  const buyerOrSeller = useBuyerOrSeller(swapDetails);

  // Used to keep the loader spinning after a successful submission while the
  // page is being redirected.
  const [submitted, setSubmitted] = useState(false);

  const isOffer =
    action === 'makeOffer' ||
    action === 'sellerOffer' ||
    action === 'counterOffer';

  let showMargin = false;
  if (item.seller_total_cost != null) {
    if (action === 'sellerOffer') {
      showMargin = true;
    } else if (action === 'counterOffer') {
      showMargin = swapDetails?.seller?.username === session?.user?.username;
    }
  }

  const showShipping =
    action === 'makeOffer' ||
    (action === 'counterOffer' &&
      swapDetails?.buyer?.username === session?.user?.username);

  const showOfferMessage =
    action === 'sellerOffer' || action === 'counterOffer';

  function calculateMargin(offerAmount: number | null): React.ReactNode {
    if (item.seller_total_cost == null) {
      return null;
    }

    if (offerAmount == null || offerAmount <= 0) {
      return <i>N/A</i>;
    }

    const margin = Math.round(
      ((offerAmount - item.seller_total_cost) / offerAmount) * 100,
    );

    if (!margin && margin !== 0) {
      return <i>N/A</i>;
    } else {
      return margin + '%';
    }
  }

  const schema = useMemo(
    () =>
      yup.object().shape({
        ...(item.variations &&
          item.variations.length > 0 && {
            selectedVariationId: yup.string().required('Required'),
          }),
        ...(isOffer && {
          offerAmount: yup
            .number()
            .typeError('Required')
            .required('Required')
            .integer('Must be a whole number'),
        }),
      }),
    [action, item],
  );

  return (
    <NewModalBody title={modalTitle(action)}>
      <Formik
        validationSchema={schema}
        validateOnChange={false}
        validateOnBlur={false}
        initialValues={
          {
            selectedVariationId: initialSelectedVariationId,
            offerAmount: null,
            offerMessage: '',
          } as MakeOfferModalFormValues
        }
        onSubmit={async (values, formikHelpers) => {
          try {
            const offerAmount = values.offerAmount!;
            const selectedVariationId = values.selectedVariationId;
            if (action === 'makeOffer') {
              await makeOffer(offerAmount, selectedVariationId, betaCheckout);
            }

            if (action === 'buyNow') {
              await buyNow(selectedVariationId, betaCheckout);
            }

            if (action === 'addToCart') {
              await onAddToCartAsync(selectedVariationId);
              onRequestClose();
            }

            if (action === 'sellerOffer') {
              if (!swapId) {
                throw new Error('Missing swapId');
              }

              await postMakeSellerOffer(
                swapId,
                offerAmount,
                selectedVariationId,
                values.offerMessage,
              );
              onRequestClose();
            }

            if (action === 'counterOffer') {
              if (!swapId) {
                throw new Error('Missing swapId');
              }

              await postMakeCounterOffer(
                swapId,
                offerAmount,
                selectedVariationId,
                values.offerMessage,
              );
              onRequestClose();
            }

            if (values.offerMessage) {
              fireSentMessage(swapDetails, buyerOrSeller, 'offer');
            }

            onSuccessfullyMadeOffer?.();
            setSubmitted(true);
          } catch (err) {
            formikHelpers.setErrors(
              formatFormikError(err, {
                errorFieldMap: {
                  amount: 'offerAmount',
                },
              }),
            );
          }
        }}
      >
        {({ handleSubmit, isSubmitting, values }) => (
          <Form>
            <Loader loading={isSubmitting || submitted}>
              <div className="mb-4 font-semibold">{item.name}</div>

              {item.variations && item.variations.length > 0 && (
                <FormikSelectField
                  label="Variation"
                  placeholder="Select an Option"
                  name="selectedVariationId"
                >
                  {item.variations.map(v => {
                    const soldOut = v.quantity == 0;

                    return (
                      <option
                        key={v.id}
                        value={v.id}
                        disabled={soldOut}
                        className={clsx('font-normal', {
                          italic: soldOut,
                        })}
                      >
                        {v.name}
                        {soldOut && ' - SOLD OUT'}
                      </option>
                    );
                  })}
                </FormikSelectField>
              )}

              {isOffer && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="offerAmount">Offer Amount</Label>

                      <div className="flex items-center">
                        <FormikNumberInput
                          autoFocus
                          id="offerAmount"
                          name="offerAmount"
                          className="mr-4"
                          placeholder={displayPrice(item.price)}
                          allowNegative={false}
                          thousandSeparator
                          prefix="$"
                          decimalScale={0}
                          inputMode="decimal"
                        />
                      </div>

                      <FormikFieldError name="offerAmount" />
                    </div>

                    {showMargin ? (
                      <div>
                        <Label>Margin</Label>
                        <div className="mt-2">
                          {calculateMargin(values.offerAmount)}
                        </div>
                      </div>
                    ) : showShipping ? (
                      <div className="mt-4 flex space-x-2">
                        <CalculateItemShipping item={item} />
                      </div>
                    ) : null}
                  </div>

                  <AuctionEndText item={item} />

                  <div className="mb-4 space-y-2">
                    <div className="text-slate-green-500 text-sm">
                      {item.auction ? 'Highest Offer' : 'Asking Price'}
                      {': '}
                      <b className="font-semibold text-black">
                        {displayPrice(item.price)}
                      </b>
                    </div>

                    {action === 'counterOffer' && swapDetails && (
                      <div className="text-slate-green-500 text-sm">
                        Their Offer:{' '}
                        <b className="font-semibold text-black">
                          {displayPrice(
                            getCurrentOffer(swapDetails)?.itemization.price,
                          )}
                        </b>
                      </div>
                    )}

                    {action === 'makeOffer' && item.interested_count! > 0 && (
                      <div className="text-slate-green-500 text-sm">
                        Interested Buyers:{' '}
                        <b className="font-semibold text-black">
                          {item.interested_count}
                        </b>
                      </div>
                    )}
                  </div>
                </>
              )}

              {showOfferMessage && (
                <FormikField
                  name="offerMessage"
                  label={
                    <>
                      Add a Message <i className="font-normal">(optional)</i>
                    </>
                  }
                >
                  <FormikTextArea
                    name="offerMessage"
                    placeholder="Say Hi! Sellers who message their buyers are 15% likelier to make the sale."
                  />
                </FormikField>
              )}

              <FormikErrorMessage />

              <div className="flex space-x-2">
                <Button
                  className="w-full"
                  variant="text"
                  onClick={onRequestClose}
                >
                  Cancel
                </Button>
                <Button
                  className="w-full"
                  type="submit"
                  // @ts-ignore
                  onClick={handleSubmit}
                >
                  {modalCTA(action)}
                </Button>
              </div>

              {action === 'counterOffer' && swapDetails && (
                <div className="mt-4">
                  <SwapActionRejectOffer swapDetails={swapDetails} />
                </div>
              )}
            </Loader>
          </Form>
        )}
      </Formik>
    </NewModalBody>
  );
};

export default MakeOfferModal;
